import { useState, useRef, useCallback, useEffect } from 'react'
import timeEntryService from '@api/v1/timeEntryService'

/**
 * VoiceButton — records audio, transcribes via Workers AI Whisper,
 * returns text via onTranscription callback.
 */
export default function VoiceButton({ onTranscription, disabled }) {
  const [state, setState] = useState('idle') // idle | recording | transcribing | error
  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])

  useEffect(() => {
    return () => {
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
      streamRef.current?.getTracks().forEach((t) => t.stop())
    }
  }, [])

  const getMimeType = useCallback(() => {
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type
    }
    return ''
  }, [])

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mimeType = getMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {})
      recorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop())
        streamRef.current = null
        if (chunksRef.current.length === 0) { setState('idle'); return }

        setState('transcribing')
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' })

        try {
          const result = await timeEntryService.transcribe(blob)
          if (result.text) onTranscription(result.text)
          setState('idle')
        } catch {
          setState('error')
          setTimeout(() => setState('idle'), 2000)
        }
      }

      recorder.start(250)
      setState('recording')
    } catch {
      setState('error')
      setTimeout(() => setState('idle'), 2000)
    }
  }, [getMimeType, onTranscription])

  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }, [])

  const handleClick = useCallback(() => {
    if (state === 'idle') startRecording()
    else if (state === 'recording') stopRecording()
  }, [state, startRecording, stopRecording])

  const isRecording = state === 'recording'
  const isTranscribing = state === 'transcribing'

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || isTranscribing}
      title={isRecording ? 'Stop recording' : isTranscribing ? 'Transcribing...' : 'Voice input'}
      className={`relative p-2.5 rounded-full transition-all disabled:opacity-50 ${
        isRecording
          ? 'bg-red-500 hover:bg-red-600 text-white'
          : state === 'error'
          ? 'bg-red-100 text-red-500'
          : 'bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700'
      }`}
    >
      {isRecording && (
        <span className="absolute inset-0 rounded-full animate-ping bg-red-400 opacity-30" />
      )}
      {isTranscribing ? (
        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : isRecording ? (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
        </svg>
      )}
    </button>
  )
}
